import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { customerService } from '../../api/apiClient';

const ALL_ROLES = ['someac_superuser', 'someac_owner', 'someac_guest', 'sabal_app_user'];

function normalizeEmailInput(input) {
  return String(input || '').trim().toLowerCase();
}

function isValidEmailInput(input) {
  const e = normalizeEmailInput(input);
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
}

export default function CreateUserDialog({ open, onClose, onCreate }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadCustomersState, setLoadCustomersState] = useState({ loading: false, options: [], error: null });
  const [form, setForm] = useState({
    sabalLoginEmail: '',
    contactEmail: '',
    displayName: '',
    customerId: '',
    roles: []
  });
  const [formError, setFormError] = useState('');

  const canSubmit = useMemo(() => {
    if (!isValidEmailInput(form.sabalLoginEmail)) return false;
    if (!form.roles.length) return false;
    if (!String(form.customerId || '').trim()) return false;
    return true;
  }, [form.customerId, form.roles, form.sabalLoginEmail]);

  // Reset form when dialog opens
  useEffect(() => {
    if (!open) return;
    setIsSubmitting(false);
    setFormError('');
    setForm({ sabalLoginEmail: '', contactEmail: '', displayName: '', customerId: '', roles: [] });
  }, [open]);

  // Load customers for selector (with fallback to free-text if it fails)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function load() {
      setLoadCustomersState({ loading: true, options: [], error: null });
      const resp = await customerService.getCustomers(500, 0);
      if (cancelled) return;
      if (!resp.success) {
        setLoadCustomersState({ loading: false, options: [], error: resp.message || 'Failed to load customers' });
        return;
      }
      const customers = resp.data?.data || resp.data || [];
      const ids = (Array.isArray(customers) ? customers : [])
        .map((c) => c.customerId)
        .filter(Boolean);
      setLoadCustomersState({ loading: false, options: ids, error: null });
    }

    load();
    return () => { cancelled = true; };
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    setFormError('');
    onClose?.();
  };

  const handleSubmit = async () => {
    setFormError('');
    setIsSubmitting(true);
    try {
      const payload = {
        sabalLoginEmail: normalizeEmailInput(form.sabalLoginEmail),
        contactEmail: form.contactEmail ? String(form.contactEmail).trim().toLowerCase() : null,
        displayName: form.displayName ? String(form.displayName).trim() : null,
        customerId: form.customerId ? String(form.customerId).trim() : null,
        roles: form.roles
      };

      // Let parent do API call so it can refresh grid and show snackbars consistently.
      const result = await onCreate?.(payload);
      if (result?.ok === false) {
        setFormError(result.message || 'Failed to create user');
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
      onClose?.();
    } catch (e) {
      setIsSubmitting(false);
      setFormError(e?.message || 'Failed to create user');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="create-user-dialog-title">
      <DialogTitle id="create-user-dialog-title">Create User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <TextField
            label="Sabal Login Email"
            value={form.sabalLoginEmail}
            onChange={(e) => setForm((p) => ({ ...p, sabalLoginEmail: e.target.value }))}
            required
            disabled={isSubmitting}
            error={!!form.sabalLoginEmail && !isValidEmailInput(form.sabalLoginEmail)}
            helperText="Enter user's email address"
            autoComplete="off"
          />

          <TextField
            label="Contact Email"
            value={form.contactEmail}
            onChange={(e) => setForm((p) => ({ ...p, contactEmail: e.target.value }))}
            disabled={isSubmitting}
            helperText="Optional (any domain)"
            autoComplete="off"
          />

          <TextField
            label="Display Name"
            value={form.displayName}
            onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
            disabled={isSubmitting}
            helperText="Optional"
          />

          <FormControl>
            <InputLabel id="create-user-roles-label">Roles</InputLabel>
            <Select
              labelId="create-user-roles-label"
              multiple
              value={form.roles}
              onChange={(e) => setForm((p) => ({ ...p, roles: e.target.value }))}
              input={<OutlinedInput label="Roles" />}
              disabled={isSubmitting}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((v) => <Chip key={v} size="small" label={v} />)}
                </Box>
              )}
            >
              {ALL_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              At least one role is required.
            </Typography>
          </FormControl>

          <Autocomplete
            freeSolo
            options={loadCustomersState.options}
            loading={loadCustomersState.loading}
            value={form.customerId || ''}
            onInputChange={(_, value) => setForm((p) => ({ ...p, customerId: value }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer ID"
                required
                disabled={isSubmitting}
                helperText={
                  loadCustomersState.error
                    ? `Could not load the customer list (${loadCustomersState.error}). You can still type a customerId.`
                    : 'Required for every user'
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadCustomersState.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />


        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting || !canSubmit} variant="contained">
          {isSubmitting ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


